/** ISO-Kalenderwochen für Materialanforderungen (Lieferfenster). */

export interface MaLieferKwRange {
  von: number;
  bis: number;
  /** ISO-Wochenjahr der ersten KW. */
  jahr: number;
}

/** ISO-Kalenderwoche (1..53). */
export function isoWeek(date: Date): number {
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayNr = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const firstDayNr = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstDayNr + 3);
  const diff = target.getTime() - firstThursday.getTime();
  return 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
}

/** ISO-Wochenjahr (kann von Kalenderjahr abweichen). */
export function isoWeekYear(date: Date): number {
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayNr = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  return target.getFullYear();
}

export function currentIsoWeekYear(now = new Date()): number {
  return isoWeekYear(now);
}

/** Montag (lokal) der ISO-KW `week` im ISO-Jahr `year`. */
export function mondayOfIsoWeek(year: number, week: number): Date {
  const jan4 = new Date(year, 0, 4);
  const dayNr = (jan4.getDay() + 6) % 7;
  const week1Monday = new Date(year, 0, 4 - dayNr);
  const monday = new Date(week1Monday);
  monday.setDate(week1Monday.getDate() + (week - 1) * 7);
  return monday;
}

/** Sonntag (lokal) der ISO-KW `week` im ISO-Jahr `year`. */
export function sundayOfIsoWeek(year: number, week: number): Date {
  const monday = mondayOfIsoWeek(year, week);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return sunday;
}

export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseIsoDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const date = new Date(y, mo - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== mo - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
}

/**
 * Parst Eingaben wie „KW32“, „32“, „KW32-KW34“, „32-34“, optional mit Jahr
 * („KW32-34/2026“, „KW32 2026“).
 */
export function parseLieferKwInput(
  raw: string,
  defaultYear: number = currentIsoWeekYear(),
): { ok: true; range: MaLieferKwRange } | { ok: false; error: string } {
  const text = raw.trim();
  if (!text) {
    return { ok: false, error: "Bitte Kalenderwochen eingeben." };
  }

  const normalized = text
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

  const withYear =
    /^(?:kw\s*)?(\d{1,2})(?:\s*(?:-|bis)\s*(?:kw\s*)?(\d{1,2}))?(?:\s*[\/,]\s*|\s+)(\d{4})$/i.exec(
      normalized,
    ) ||
    /^(?:kw\s*)?(\d{1,2})(?:\s*(?:-|bis)\s*(?:kw\s*)?(\d{1,2}))?$/i.exec(
      normalized,
    );

  if (!withYear) {
    return {
      ok: false,
      error: "Format z. B. KW32 oder KW32-KW34 (optional /Jahr).",
    };
  }

  const von = Number(withYear[1]);
  const bis = withYear[2] != null ? Number(withYear[2]) : von;
  const jahr = withYear[3] != null ? Number(withYear[3]) : defaultYear;

  if (!Number.isInteger(von) || !Number.isInteger(bis)) {
    return { ok: false, error: "Kalenderwochen müssen ganze Zahlen sein." };
  }
  if (von < 1 || von > 53 || bis < 1 || bis > 53) {
    return { ok: false, error: "Kalenderwochen müssen zwischen 1 und 53 liegen." };
  }
  if (von > bis) {
    return { ok: false, error: "Die erste KW darf nicht nach der letzten liegen." };
  }
  if (jahr < 2000 || jahr > 2100) {
    return { ok: false, error: "Jahr ist ungültig." };
  }

  // Prüfen, ob die KW im Jahr existiert (z. B. KW53 nicht immer).
  const mondayVon = mondayOfIsoWeek(jahr, von);
  if (isoWeekYear(mondayVon) !== jahr || isoWeek(mondayVon) !== von) {
    return { ok: false, error: `KW${von} gibt es ${jahr} nicht.` };
  }
  const mondayBis = mondayOfIsoWeek(jahr, bis);
  if (isoWeekYear(mondayBis) !== jahr || isoWeek(mondayBis) !== bis) {
    return { ok: false, error: `KW${bis} gibt es ${jahr} nicht.` };
  }

  return { ok: true, range: { von, bis, jahr } };
}

export function formatLieferKw(
  range: MaLieferKwRange | null | undefined,
  options?: { asciiHyphen?: boolean; forceYear?: boolean },
): string {
  if (!range || range.von == null || range.bis == null) return "";
  const sep = options?.asciiHyphen ? "-" : "–";
  const base =
    range.von === range.bis
      ? `KW${range.von}`
      : `KW${range.von}${sep}KW${range.bis}`;
  const current = currentIsoWeekYear();
  if (range.jahr && (options?.forceYear || range.jahr !== current)) {
    return `${base}/${range.jahr}`;
  }
  return base;
}

export function formatLieferKwFromFields(
  von: number | null | undefined,
  bis: number | null | undefined,
  jahr: number | null | undefined,
  options?: { asciiHyphen?: boolean; forceYear?: boolean },
): string {
  if (von == null || bis == null || jahr == null) return "";
  return formatLieferKw({ von, bis, jahr }, options);
}

/** Fixtermin muss innerhalb [Montag erste KW … Sonntag letzte KW] liegen. */
export function isFixterminWithinLieferKw(
  fixIso: string,
  range: MaLieferKwRange,
): boolean {
  const day = parseIsoDate(fixIso);
  if (!day) return false;
  const min = mondayOfIsoWeek(range.jahr, range.von);
  const max = sundayOfIsoWeek(range.jahr, range.bis);
  const t = day.getTime();
  return t >= min.getTime() && t <= max.getTime();
}

export function lieferKwDateBounds(range: MaLieferKwRange): {
  minIso: string;
  maxIso: string;
} {
  return {
    minIso: toIsoDate(mondayOfIsoWeek(range.jahr, range.von)),
    maxIso: toIsoDate(sundayOfIsoWeek(range.jahr, range.bis)),
  };
}
